import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ContactForm from "../components/ContactForm";
import Shop from "../components/Shop";
import { useLanguage } from "../i18n/LanguageContext";
import { Leaf, Sparkles, Compass, Quote } from "lucide-react";

const heroImg = "https://customer-assets.emergentagent.com/job_self-mastery-hub-8/artifacts/dqc1vl2t_IMG-20260607-WA0034.jpg";
const aboutImg = "https://customer-assets.emergentagent.com/job_self-mastery-hub-8/artifacts/uzvlv3qd_IMG-20260607-WA0045.jpg";

const icons = [Sparkles, Leaf, Compass];

const WomenPage = () => {
  const { t } = useLanguage();
  const w = t.women;

  return (
    <div data-testid="women-page" className="theme-women min-h-screen">
      <Header variant="women" />

      {/* HERO */}
      <section className="px-6 md:px-12 lg:px-20 pt-10 md:pt-16 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7 fade-up">
          <span className="eyebrow text-[#5C6660]">{w.hero.eyebrow}</span>
          <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl leading-[1.08] text-[#2C332E]">
            {w.hero.title}
          </h1>
          <p className="mt-7 text-base md:text-lg text-[#5C6660] max-w-2xl leading-relaxed">
            {w.hero.sub}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a data-testid="women-hero-cta-primary" href="#contact" className="btn-sage">{w.hero.ctaPrimary}</a>
            <a data-testid="women-hero-cta-secondary" href="#pillars" className="btn-ghost-light">{w.hero.ctaSecondary}</a>
          </div>
        </div>
        <div className="lg:col-span-5 fade-up delay-2">
          <div className="relative aspect-[4/5] w-full rounded-3xl overflow-hidden bg-[#EBE7E0]">
            <img
              src={heroImg}
              alt="Colleen Schmidt — Lebensfreude im Sonnenblumenfeld"
              className="w-full h-full object-cover"
              style={{ filter: "saturate(0.95) brightness(1.0) contrast(0.98)", objectPosition: "center 35%" }}
            />
            <div className="absolute inset-0 ring-1 ring-[#D5CECA] rounded-3xl pointer-events-none" />
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="px-6 md:px-12 lg:px-20 py-20 border-t border-[#D5CECA]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="eyebrow text-[#5C6660]">{w.problem.eyebrow}</span>
            <h2 className="mt-5 text-3xl md:text-4xl leading-tight text-[#2C332E]">
              {w.problem.title}
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-base md:text-lg text-[#5C6660] leading-relaxed">{w.problem.body}</p>
            <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
              {w.problem.bullets.map((b, i) => (
                <li key={i} data-testid={`women-bullet-${i}`} className="flex items-start gap-3 text-[#2C332E]">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#738678]" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="relative px-6 md:px-12 lg:px-20 py-20 border-t border-[#D5CECA] overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center opacity-[0.10] pointer-events-none"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1542038784456-1ea8e935640e?auto=format&fit=crop&w=1600&q=80')", filter: "saturate(0.7) sepia(0.15)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#F4F1ED] via-[#F4F1ED]/85 to-[#F4F1ED] pointer-events-none" />
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden">
              <img
                src={aboutImg}
                alt="Colleen Schmidt"
                className="w-full h-full object-cover"
                style={{ filter: "saturate(0.88) brightness(1.02) contrast(1.0)", objectPosition: "center 30%" }}
              />
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <span className="eyebrow text-[#5C6660]">{w.about.eyebrow}</span>
            <h2 className="mt-5 text-3xl md:text-4xl leading-tight text-[#2C332E]">{w.about.title}</h2>
            <p className="mt-6 text-base md:text-lg text-[#5C6660] leading-relaxed">{w.about.body}</p>
            <blockquote className="mt-10 flex items-start gap-4 text-[#2C332E]">
              <Quote className="text-[#738678] mt-1 shrink-0" />
              <span className="text-xl md:text-2xl italic leading-snug" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                {w.about.quote}
              </span>
            </blockquote>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section id="pillars" className="relative px-6 md:px-12 lg:px-20 py-20 border-t border-[#D5CECA] overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center opacity-[0.08] pointer-events-none"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1591197172062-c718f82aba20?auto=format&fit=crop&w=1600&q=80')", filter: "saturate(0.6) sepia(0.18)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#F4F1ED]/95 via-[#F4F1ED]/85 to-[#F4F1ED] pointer-events-none" />
        <div className="relative max-w-3xl">
          <span className="eyebrow text-[#5C6660]">{w.pillars.eyebrow}</span>
          <h2 className="mt-5 text-3xl md:text-4xl text-[#2C332E]">{w.pillars.title}</h2>
        </div>
        <div className="relative mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {w.pillars.items.map((p, i) => {
            const Icon = icons[i] || Sparkles;
            return (
              <div
                key={p.id}
                data-testid={`women-pillar-${p.id}`}
                className="bg-[#EBE7E0] border border-[#D5CECA] rounded-2xl p-8 hover:bg-[#E3DDD3] transition group"
              >
                <Icon size={28} className="text-[#738678] mb-6 transition-transform group-hover:-translate-y-1" />
                <h3 className="text-xl md:text-2xl text-[#2C332E] leading-snug">{p.title}</h3>
                <p className="mt-4 text-[#5C6660] leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* PROOF */}
      <section className="px-6 md:px-12 lg:px-20 py-20 border-t border-[#D5CECA]">
        <span className="eyebrow text-[#5C6660]">{w.proof.eyebrow}</span>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {w.proof.items.map((q, i) => (
            <figure key={i} data-testid={`women-quote-${i}`} className="p-8 bg-[#EBE7E0]/60 border border-[#D5CECA] rounded-2xl">
              <Quote className="text-[#738678]" />
              <blockquote className="mt-4 text-[#2C332E] italic text-lg leading-relaxed" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                „{q}"
              </blockquote>
            </figure>
          ))}
        </div>
      </section>

      {/* SHOP */}
      <Shop pathKey="women" />

      {/* BRIDGE — Inclusion */}
      <section className="relative px-6 md:px-12 lg:px-20 py-20 border-t border-[#D5CECA] overflow-hidden">
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="eyebrow text-[#5C6660]">{w.bridge.eyebrow}</span>
            <h2 className="mt-5 text-3xl md:text-4xl leading-tight text-[#2C332E]">{w.bridge.title}</h2>
            <p className="mt-6 text-base md:text-lg text-[#5C6660] leading-relaxed">{w.bridge.body}</p>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-[#D5CECA] bg-[#EBE7E0]/50 p-8 rounded-2xl">
              <p className="text-sm uppercase tracking-[0.22em] text-[#5C6660]">{w.bridge.crossEyebrow}</p>
              <Link
                to="/men"
                data-testid="women-cross-link"
                className="mt-4 inline-flex items-center gap-2 text-[#2C332E] hover:text-[#738678] transition group"
                style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.5rem" }}
              >
                {w.bridge.crossLink}
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA / CONTACT */}
      <section id="contact" className="relative px-6 md:px-12 lg:px-20 py-20 border-t border-[#D5CECA] overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center opacity-[0.10] pointer-events-none"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1499744937866-d7e566a20a61?auto=format&fit=crop&w=1600&q=80')", filter: "saturate(0.7) sepia(0.18)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#F4F1ED] via-[#F4F1ED]/90 to-[#F4F1ED]/95 pointer-events-none" />
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="eyebrow text-[#5C6660]">{w.cta.eyebrow}</span>
            <h2 className="mt-5 text-3xl md:text-4xl leading-tight text-[#2C332E]">{w.cta.title}</h2>
            <p className="mt-6 text-base md:text-lg text-[#5C6660] leading-relaxed">{w.cta.sub}</p>
            {w.cta.investmentNote && (
              <p className="mt-4 text-sm text-[#738678] italic">{w.cta.investmentNote}</p>
            )}
          </div>
          <div className="lg:col-span-7">
            <h3 className="text-lg md:text-xl text-[#2C332E] mb-6" style={{ fontFamily: "Cormorant Garamond, serif" }}>{w.cta.formTitle}</h3>
            <ContactForm pathKey="women" />
          </div>
        </div>
      </section>

      <Footer variant="women" />
    </div>
  );
};

export default WomenPage;
